import { SidebarTrigger, useSidebar } from '@/components/ui/sidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Target, CheckCircle2, Circle, AlertCircle } from 'lucide-react';
import SEO from '@/components/SEO';

const mockSkills = [
  { name: 'HTML/CSS', status: 'acquired', proficiency: 90, source: 'From Training' },
  { name: 'JavaScript', status: 'in-progress', proficiency: 60, source: 'From Module 3' },
  { name: 'React', status: 'gap', proficiency: 0, source: 'Required' },
  { name: 'TypeScript', status: 'gap', proficiency: 0, source: 'Required' },
  { name: 'Git/GitHub', status: 'acquired', proficiency: 75, source: 'From Training' },
  { name: 'RESTful APIs', status: 'in-progress', proficiency: 40, source: 'From Module 4' },
  { name: 'Node.js', status: 'gap', proficiency: 0, source: 'Required' },
  { name: 'Database Design', status: 'gap', proficiency: 0, source: 'Required' },
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'acquired':
      return 'default';
    case 'in-progress':
      return 'secondary';
    case 'gap':
      return 'destructive';
    default:
      return 'outline';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'acquired':
      return <CheckCircle2 className="h-4 w-4 text-primary" />;
    case 'in-progress':
      return <AlertCircle className="h-4 w-4 text-secondary" />;
    case 'gap':
      return <Circle className="h-4 w-4 text-muted-foreground" />;
    default:
      return null;
  }
};

export default function SkillMap() {
  const { isMobile } = useSidebar();

  const acquiredCount = mockSkills.filter(s => s.status === 'acquired').length;
  const totalRequired = mockSkills.length;
  const completionPercentage = Math.round((acquiredCount / totalRequired) * 100);

  return (
    <div className="flex flex-col flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 bg-background text-foreground space-y-6">
      <SEO title="Skill Map - VeteranOS" description="Visual gap analysis of your technical skills" />
      
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-border pb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-primary">Skill Map</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Visual gap analysis: Your background vs. target role requirements
          </p>
        </div>
        <div className="flex items-center gap-4 mt-4 md:mt-0">
          {isMobile && <SidebarTrigger />}
        </div>
      </div>

      {/* Progress Summary */}
      <Card accent>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Overall Skill Acquisition
          </CardTitle>
          <CardDescription>
            {acquiredCount} of {totalRequired} required skills acquired or in progress
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-semibold text-foreground">{completionPercentage}%</span>
            </div>
            <Progress value={completionPercentage} className="h-3" />
          </div>
        </CardContent>
      </Card>

      {/* Skills Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {mockSkills.map((skill, idx) => (
          <Card key={idx} className="transition-colors duration-200 hover:shadow-md">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  {getStatusIcon(skill.status)}
                  <CardTitle className="text-base">{skill.name}</CardTitle>
                </div>
                <Badge variant={getStatusColor(skill.status)}>
                  {skill.status === 'acquired' && 'Acquired'}
                  {skill.status === 'in-progress' && 'In Progress'}
                  {skill.status === 'gap' && 'Gap'}
                </Badge>
              </div>
              <CardDescription className="text-xs">{skill.source}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Proficiency</span>
                  <span className="font-medium text-foreground">{skill.proficiency}%</span>
                </div>
                <Progress value={skill.proficiency} className="h-2" />
                {skill.status === 'gap' && (
                  <Button variant="outline" size="sm" className="w-full mt-2">
                    Start Learning
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
